import { REHYDRATE } from 'redux-persist'
import {
  all,
  call,
  fork,
  put,
  SagaReturnType,
  select,
  takeEvery,
  takeLeading,
} from 'redux-saga/effects'

import { actions, selectors } from '..'
import { services } from '../../graphql'
import ApiSagas from '../api/sagas'
import {
  PublicExpoEventBySlugQueryVariables,
  PublicExpoRegisterMutationVariables,
  PublicExpoSendVerificationCodeMutationVariables,
  AppUnregisterDataQueryVariables,
  PublicExpoAuthenticateVisitorMutationVariables,
  PublicExpoUpdateRegistrationMutationVariables,
  AppUnregisterMutationVariables,
} from '../../graphql/types/api'
import { ApiResponse } from '../api/types/state'
import i18nInstance, { resources } from '../../i18n'

export default class AppSagas {
  static *init() {
    const isInit: SagaReturnType<typeof selectors.app.isInit> = yield select(selectors.app.isInit)
    yield fork(AppSagas.loadInitialData) // can be forked

    if (!isInit) {
      yield put(actions.app.setIsInit({ isInit: true }))
    }
  }

  static *loadInitialData() {
    yield all([])
  }

  static *switchLocale(action: ReturnType<typeof actions.app.switchLocale>) {
    yield put(actions.app.setCurrentLocale(action.payload))

    // refresh event data

    const getEventBySlug: ReturnType<typeof selectors.app.getEventBySlug> = yield select(
      selectors.app.getEventBySlug
    )

    if (getEventBySlug.data?.slug) {
      yield put(actions.app.getEventBySlugRequest({ slug: getEventBySlug.data?.slug }))
    }
  }

  static *setCurrentLocale(_action: ReturnType<typeof actions.app.setCurrentLocale>) {
    yield put(actions.app.getLabelsRequest())
  }

  static *getEventBySlugRequest({ payload }: ReturnType<typeof actions.app.getEventBySlugRequest>) {
    const response: ApiResponse<typeof services.app.queries.eventBySlug> = yield call(
      ApiSagas.query,
      services.app.queries.eventBySlug,
      {
        ...payload,
      } as PublicExpoEventBySlugQueryVariables
    )

    if (response.data) {
      yield put(actions.app.getEventBySlugSuccess(response.data))

      // change local if no labels already loaded
      const getLabels: ReturnType<typeof selectors.app.getLabels> = yield select(
        selectors.app.getLabels
      )
      if (!getLabels?.data) {
        yield put(actions.app.setCurrentLocale(response.data.locales?.[0]?.iso ?? 'en'))
      }
    } else {
      yield put(actions.app.getEventBySlugError(response.errors))
    }
  }

  static *getLabelsRequest(_action: ReturnType<typeof actions.app.getLabelsRequest>) {
    const response: ApiResponse<typeof services.app.queries.labels> = yield call(
      ApiSagas.query,
      services.app.queries.labels
    )

    if (response.data) {
      const currentLocale: ReturnType<typeof selectors.app.currentLocale> = yield select(
        selectors.app.currentLocale
      )

      const wordings = response.data.reduce(
        (previousValue, currentValue) => ({
          ...previousValue,
          [currentValue.key]: currentValue.value,
        }),
        {}
      )

      i18nInstance.addResourceBundle(
        currentLocale,
        'translation',
        {
          ...(resources as any)?.[currentLocale]?.translation,
          ...wordings,
        },
        true,
        true
      )

      i18nInstance.changeLanguage(currentLocale)

      yield put(actions.app.getLabelsSuccess(response.data))
    } else {
      yield put(actions.app.getLabelsError(response.errors))
    }
  }

  static *sendVerificationCodesRequest({
    payload,
  }: ReturnType<typeof actions.app.sendVerificationCodesRequest>) {
    const response: ApiResponse<typeof services.app.mutations.sendVerificationCodes> = yield call(
      ApiSagas.mutate,
      services.app.mutations.sendVerificationCodes,
      {
        ...payload,
      } as PublicExpoSendVerificationCodeMutationVariables
    )

    if (response.data) {
      yield put(actions.app.sendVerificationCodesSuccess(response.data))
    } else {
      yield put(actions.app.sendVerificationCodesError(response.errors))
    }
  }

  static *registerRequest({ payload }: ReturnType<typeof actions.app.registerRequest>) {
    const response: ApiResponse<typeof services.app.mutations.register> = yield call(
      ApiSagas.mutate,
      services.app.mutations.register,
      {
        ...payload,
      } as PublicExpoRegisterMutationVariables
    )

    if (response.data) {
      const dl = (window as any).dataLayer
      if (dl) {
        dl.push({
          event: 'register',
        })
      }
      yield put(actions.app.registerSuccess(response.data))
    } else {
      yield put(actions.app.registerError(response.errors))
    }
  }

  static *unregisterRequest({ payload }: ReturnType<typeof actions.app.unregisterRequest>) {
    const response: ApiResponse<typeof services.app.mutations.unregister> = yield call(
      ApiSagas.mutate,
      services.app.mutations.unregister,
      {
        ...payload,
      } as AppUnregisterMutationVariables
    )

    if (response.data) {
      yield put(actions.app.unregisterSuccess(response.data))
    } else {
      yield put(actions.app.unregisterError(response.errors))
    }
  }

  static *getUnregisterRequest({
    payload,
  }: ReturnType<typeof actions.app.getUnregisterDataRequest>) {
    const response: ApiResponse<typeof services.app.queries.unregisterData> = yield call(
      ApiSagas.query,
      services.app.queries.unregisterData,
      {
        ...payload,
      } as AppUnregisterDataQueryVariables
    )

    if (response.data) {
      yield put(actions.app.getUnregisterDataSuccess(response.data))
    } else {
      yield put(actions.app.getUnregisterDataError(response.errors))
    }
  }

  static *getCurrentEventRequest({
    payload,
  }: ReturnType<typeof actions.app.getCurrentEventRequest>) {
    const response: ApiResponse<typeof services.app.queries.currentEvent> = yield call(
      ApiSagas.query,
      services.app.queries.currentEvent,
      {
        ...payload,
      }
    )

    if (response.data) {
      yield put(actions.app.getCurrentEventSuccess(response.data))
    } else {
      yield put(actions.app.getCurrentEventError(response.errors))
    }
  }

  static *authenticateVisitorRequest({
    payload,
  }: ReturnType<typeof actions.app.authenticateVisitorRequest>) {
    const response: ApiResponse<typeof services.app.mutations.authenticateVisitor> = yield call(
      ApiSagas.mutate,
      services.app.mutations.authenticateVisitor,
      {
        ...payload,
      } as PublicExpoAuthenticateVisitorMutationVariables
    )

    if (response.data) {
      yield put(actions.app.setAuthenticationToken(response.data.token))
      yield put(actions.app.authenticateVisitorSuccess(response.data))
    } else {
      yield put(actions.app.authenticateVisitorError(response.errors))
    }
  }

  static *updateRegistrationRequest({
    payload,
  }: ReturnType<typeof actions.app.updateRegistrationRequest>) {
    const response: ApiResponse<typeof services.app.mutations.updateRegistration> = yield call(
      ApiSagas.mutate,
      services.app.mutations.updateRegistration,
      {
        ...payload,
      } as PublicExpoUpdateRegistrationMutationVariables
    )

    if (response.data) {
      yield put(actions.app.updateRegistrationSuccess(response.data))
    } else {
      yield put(actions.app.updateRegistrationError(response.errors))
    }
  }

  static *deleteRegistrationRequest() {
    const response: ApiResponse<typeof services.app.mutations.deleteRegistration> = yield call(
      ApiSagas.mutate,
      services.app.mutations.deleteRegistration
    )

    if (response.data) {
      yield put(actions.app.deleteRegistrationSuccess(response.data))
    } else {
      yield put(actions.app.deleteRegistrationError(response.errors))
    }
  }

  static *listeners() {
    yield all([
      takeLeading(REHYDRATE, this.init),
      takeEvery(actions.app.switchLocale, this.switchLocale),
      takeEvery(actions.app.setCurrentLocale, this.setCurrentLocale),
      takeEvery(actions.app.getEventBySlugRequest, this.getEventBySlugRequest),
      takeEvery(actions.app.getLabelsRequest, this.getLabelsRequest),
      takeEvery(actions.app.sendVerificationCodesRequest, this.sendVerificationCodesRequest),
      takeEvery(actions.app.registerRequest, this.registerRequest),
      takeEvery(actions.app.unregisterRequest, this.unregisterRequest),
      takeEvery(actions.app.getUnregisterDataRequest, this.getUnregisterRequest),
      takeEvery(actions.app.getCurrentEventRequest, this.getCurrentEventRequest),
      takeEvery(actions.app.authenticateVisitorRequest, this.authenticateVisitorRequest),
      takeEvery(actions.app.updateRegistrationRequest, this.updateRegistrationRequest),
      takeEvery(actions.app.deleteRegistrationRequest, this.deleteRegistrationRequest),
    ])
  }
}
